import { useCallback, useEffect, useRef, useState } from "react";
import SheAiChecklistService from "../../services/sheai/SheAiChecklistService";
import {
  AiAnalysis,
  AiChecklistItem,
  AiItemFeedback,
} from "../../services/sheai/SheAiChecklistTypes";

/**
 * A análise da IA de um Debriefing, consultada uma vez por tela.
 *
 * Existe porque as duas telas que a consomem querem recortes opostos do MESMO
 * `GET /sheai/checklist/result/{id}`: a aba Checklist usa só a dica por item
 * (`porItem`), a aba Dados IA usa o envelope inteiro. Antes cada uma repetia o
 * fetch, o polling e o "cadê o resultado dentro de content[0]".
 *
 * O polling só roda enquanto o agente está processando; quem dispara a análise
 * é o backend (Pub/Sub quando o MP4 fica disponível), não esta tela.
 */

const POLL_INTERVAL_MS = 10000;
const POLL_TIMEOUT_MS = 10 * 60 * 1000; // o agente real leva ~6 min

export interface ChecklistAiResult {
  /** not_found | in_progress | finished | failed | "" enquanto carrega */
  status: string;
  msg: string;
  progress: number;
  analise: AiAnalysis | null;
  /** Itens indexados por `item_id` em string — a chave que o checklist usa. */
  porItem: Record<string, AiChecklistItem>;
  /** Revisão já registrada pelo professor, por `item_id`. */
  revisao: Record<string, AiItemFeedback>;
  erro: boolean;
  recarregar: () => Promise<void>;
  /**
   * Marca a análise como "em processamento" para o polling voltar a rodar.
   * Quem chama é o botão Reprocessar, depois do POST que republica o vídeo:
   * o agente grava um JSON novo no bucket e o próximo `GET /result` já o
   * devolve (o espelho no banco é invalidado por `job_id`).
   */
  aguardarNovaAnalise: () => void;
}

export function useChecklistAiResult(
  debriefingId: string | number | undefined
): ChecklistAiResult {
  const [status, setStatus] = useState("");
  const [msg, setMsg] = useState("");
  const [progress, setProgress] = useState(0);
  const [analise, setAnalise] = useState<AiAnalysis | null>(null);
  const [revisao, setRevisao] = useState<Record<string, AiItemFeedback>>({});
  const [erro, setErro] = useState(false);
  const inicioRef = useRef(0);
  /** Timestamp da análise ANTES de pedir reprocessamento. Enquanto o bucket
      devolver este mesmo arquivo, o polling continua — a análise velha não
      pode posar de nova só porque o `GET /result` devolve `finished`. */
  const antesDoReprocessoRef = useRef<string | null>(null);
  /** Guarda de corrida: navegar entre avaliados/debriefings troca o id antes
      de a resposta anterior chegar. Sem esta checagem, a resposta ATRASADA do
      debriefing antigo sobrescreveria a análise do novo. */
  const idAtualRef = useRef(debriefingId);
  idAtualRef.current = debriefingId;

  const recarregar = useCallback(async () => {
    if (!debriefingId) return;
    try {
      const { data } = await SheAiChecklistService.getChecklistResult(
        debriefingId
      );
      if (idAtualRef.current !== debriefingId) return;
      if (!data) return;
      let rawResult = data.content?.[0]?.result || null;
      while (Array.isArray(rawResult) && rawResult.length > 0) {
        rawResult = rawResult[0]?.result ?? rawResult[0];
      }
      const nova: AiAnalysis | null = rawResult;

      // Pós-reprocesso: `finished` com o MESMO arquivo de antes é a análise
      // velha — o agente ainda está rodando. Continua `in_progress`.
      const tsNova = nova?.analysis_ts ?? nova?.received_at ?? null;
      if (
        antesDoReprocessoRef.current !== null &&
        tsNova !== null &&
        tsNova === antesDoReprocessoRef.current
      ) {
        setStatus("in_progress");
        return;
      }
      antesDoReprocessoRef.current = null;

      setStatus(data.status || "");
      setMsg(data.msg || "");
      setProgress(data.progress || 0);
      setAnalise(nova);
      setErro(false);
    } catch (e) {
      if (idAtualRef.current !== debriefingId) return;
      setErro(true);
    }
  }, [debriefingId]);

  const recarregarRevisao = useCallback(async () => {
    if (!debriefingId) return;
    try {
      const { data } = await SheAiChecklistService.getChecklistFeedback(
        debriefingId
      );
      if (idAtualRef.current !== debriefingId) return;
      setRevisao(data || {});
    } catch (e) {
      // Revisão é acessório: sem ela a tela ainda avalia. Não vira erro de tela.
    }
  }, [debriefingId]);

  useEffect(() => {
    inicioRef.current = Date.now();
    recarregar();
    recarregarRevisao();
  }, [recarregar, recarregarRevisao]);

  useEffect(() => {
    if (status !== "in_progress") return;
    const timer = setInterval(() => {
      if (Date.now() - inicioRef.current > POLL_TIMEOUT_MS) {
        clearInterval(timer);
        setMsg(
          "Tempo de espera excedido. Recarregue a página para verificar novamente."
        );
        return;
      }
      recarregar();
    }, POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [status, recarregar]);

  const porItem: Record<string, AiChecklistItem> = {};
  (analise?.items || []).forEach((item) => {
    porItem[String(item.item_id)] = item;
  });

  const aguardarNovaAnalise = useCallback(() => {
    antesDoReprocessoRef.current =
      analise?.analysis_ts ?? analise?.received_at ?? null;
    setStatus("in_progress");
    setMsg("Análise solicitada — o agente está processando o vídeo novamente.");
    setProgress(0);
    inicioRef.current = Date.now();
  }, [analise]);

  return {
    status,
    msg,
    progress,
    analise,
    porItem,
    revisao,
    erro,
    recarregar,
    aguardarNovaAnalise,
  };
}

export default useChecklistAiResult;
